import React from "react";
import { Form, Input, Select, TextArea, Checkbox } from "semantic-ui-react";
import "./Paso6Tramite.scss";

const fFecha = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("es-AR") : "—");

const ESTADOS = [
  { key: "preparacion", value: "preparacion", text: "Preparación" },
  { key: "comprometida", value: "comprometida", text: "Comprometida" },
  { key: "presentada", value: "presentada", text: "Presentada" },
  { key: "revision", value: "revision", text: "En revisión" },
  { key: "aprobada", value: "aprobada", text: "Aprobada" },
  { key: "anunciada", value: "anunciada", text: "Anunciada" },
];

// Portado de DOCS del prototipo (línea 903) — ids literales, ver
// convención en sbtiRecord.js (mismo criterio que los IDs de criterios).
export const DOCS = [
  ["d_form", "Formulario de presentación completo en el Validation Portal (según el Corporate Submission Manual, o el formulario PYME si corresponde)"],
  ["d_inv", "Inventario GEI completo del año base y del año más reciente: A1 por fuente, A2 con ambos enfoques, A3 por categoría"],
  ["d_evi", "Respaldo de evidencias disponible: facturas de energía y combustibles, manifiestos, certificados de electricidad renovable"],
  ["d_exc", "Justificación documentada de exclusiones (≤ 5% A1+A2) y de la exclusión del uso indirecto Cat 11, si se aplicó"],
  ["d_scr", "Detalle del screening A3: método de cálculo por categoría y selección de cobertura ≥ 67%"],
  ["d_mod", "Modelado del objetivo con la herramienta oficial de target-setting del SBTi"],
  ["d_a2", "Enfoque de A2 declarado (market/location) y consistente entre línea base y seguimiento"],
  ["d_flag", "Si FLAG ≥ 20%: objetivo FLAG modelado y compromiso público de no-deforestación"],
  ["d_gov", "Aprobación interna de dirección/directorio y designación de contacto ante SBTi Services"],
];

const TIMELINE = [
  { id: 1, tt: "1 · Registro", dd: "Alta de la empresa en el SBTi Services Validation Portal (proceso digitalizado)." },
  { id: 2, tt: "2 · Compromiso (opcional · solo corporativa)", dd: "Carta/solicitud de compromiso; figura como «Committed» en el Target Dashboard y abre 24 meses para presentar." },
  { id: 3, tt: "3 · Desarrollo", dd: "Objetivos según criterios y guía sectorial (Pasos 1–5 de este módulo) + modelado con la herramienta oficial." },
  { id: 4, tt: "4 · Presentación y pago", dd: "Formularios del portal + tarifa. La evaluación usa los Criteria Assessment Indicators." },
  { id: 5, tt: "5 · Revisión y decisión", dd: "Indicativo de mercado 2026 (no oficial): la revisión suele comenzar ~3–4 meses después de presentar." },
  { id: 6, tt: "6 · Comunicación", dd: "Publicación en el Target Dashboard, a elección de la empresa dentro de la ventana." },
  { id: 7, tt: "7 · Divulgación anual + revisión a 5 años", dd: "Progreso público cada año (Paso 7) y revisión obligatoria de objetivos a los 5 años." },
];

// Portado de vP6() del prototipo (línea 914).
export function Paso6Tramite({ record, computed, patchSection }) {
  const v = record.validation || {};
  const params = computed?.params || {};
  const dueDates = computed?.dueDates || [];
  const isSme = record.route?.type === "sme";

  const setValidation = (patch, opts) => patchSection("validation", { ...v, ...patch }, opts);
  const setDoc = (docId, checked) =>
    setValidation({ documents: { ...(v.documents || {}), [docId]: checked } }, { immediate: true });

  const timelineState = (id) => {
    if (id === 1) return v.registeredOn ? "done" : v.status === "preparacion" ? "now" : "";
    if (id === 2) return v.committedOn ? "done" : "";
    if (id === 3) return record.targets?.scope12?.targetYear ? "done" : "";
    if (id === 4) return v.submittedOn ? "done" : "";
    if (id === 5) return v.approvedOn ? "done" : v.status === "revision" ? "now" : "";
    if (id === 6) return v.announcedOn ? "done" : v.approvedOn ? "now" : "";
    return "";
  };

  return (
    <div className="sbti-paso6">
      <div className="sbti-pagehead">
        <div className="sbti-crumb">Paso 6 · Validación</div>
        <h2>Presentación, validación y <span className="sbti-pgram sbti-pgram--dark"><span>fechas clave</span></span></h2>
        <p className="sbti-sub">
          Gestión del expediente ante SBTi Services: proceso oficial, documentación, tarifas y todos los
          vencimientos calculados automáticamente a partir de las fechas reales del trámite.
        </p>
      </div>

      <div className="sbti-card">
        <h3><span className="sbti-tag">ESTADO</span> Seguimiento del trámite</h3>
        <Form>
          <Form.Group widths={3}>
            <Form.Field>
              <label>Estado actual</label>
              <Select options={ESTADOS} value={v.status || "preparacion"} onChange={(_, d) => setValidation({ status: d.value }, { immediate: true })} />
            </Form.Field>
            <Form.Field>
              <label>Fecha de registro en el portal</label>
              <Input type="date" value={v.registeredOn || ""} onChange={(_, d) => setValidation({ registeredOn: d.value })} />
            </Form.Field>
            <Form.Field>
              <label>Fecha de compromiso (opcional)</label>
              <Input type="date" value={v.committedOn || ""} disabled={isSme} onChange={(_, d) => setValidation({ committedOn: d.value })} />
              {isSme ? (
                <div className="sbti-hint"><b>Ruta PYME:</b> no elegible para comprometerse — se presenta directo.</div>
              ) : (
                <div className="sbti-hint">Si se compromete, abre ventana de <b>24 meses</b> para presentar.</div>
              )}
            </Form.Field>
            <Form.Field>
              <label>Fecha de presentación</label>
              <Input type="date" value={v.submittedOn || ""} onChange={(_, d) => setValidation({ submittedOn: d.value })} />
            </Form.Field>
            <Form.Field>
              <label>Inicio de la revisión</label>
              <Input type="date" value={v.reviewStartedOn || ""} onChange={(_, d) => setValidation({ reviewStartedOn: d.value })} />
            </Form.Field>
            <Form.Field>
              <label>Fecha de aprobación</label>
              <Input type="date" value={v.approvedOn || ""} onChange={(_, d) => setValidation({ approvedOn: d.value })} />
            </Form.Field>
            <Form.Field>
              <label>Fecha de anuncio público</label>
              <Input type="date" value={v.announcedOn || ""} onChange={(_, d) => setValidation({ announcedOn: d.value })} />
            </Form.Field>
          </Form.Group>
          <Form.Field>
            <label>Notas del proceso (pedidos de revisión, consultas técnicas)</label>
            <TextArea rows={2} value={v.processNotes || ""} onChange={(_, d) => setValidation({ processNotes: d.value })} />
          </Form.Field>
        </Form>

        {dueDates.length > 0 && (
          <table className="sbti-plain-table" style={{ marginTop: 12 }}>
            <thead><tr><th>Vencimiento calculado</th><th>Fecha límite</th><th>Base normativa</th></tr></thead>
            <tbody>
              {dueDates.map((d) => (
                <tr key={d.label}><td>{d.label}</td><td className="num"><b>{fFecha(d.date)}</b></td><td>{d.basis}</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="sbti-dash__grid2">
        <div className="sbti-card">
          <h3>Cronología del trámite</h3>
          <div className="sbti-tl">
            {TIMELINE.map((item) => (
              <div className={`sbti-tlitem sbti-tlitem--${timelineState(item.id)}`} key={item.id}>
                <div className="sbti-tlitem__tt">{item.tt}</div>
                <div className="sbti-tlitem__dd">{item.dd}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="sbti-card">
          <h3>Tarifas de validación</h3>
          <table className="sbti-plain-table">
            <thead><tr><th>Ruta</th><th>Tarifa</th></tr></thead>
            <tbody>
              <tr><td><b>PYME</b> (streamlined)</td><td>{params.sme_fee || "—"}</td></tr>
              <tr><td><b>Corporativa</b></td><td>{params.corp_fee || "—"}</td></tr>
            </tbody>
          </table>
          <Form>
            <Form.Field style={{ marginTop: 10 }}>
              <label>Nota interna de tarifa (cotización recibida)</label>
              <Input value={v.feeNote || ""} onChange={(_, d) => setValidation({ feeNote: d.value })} />
            </Form.Field>
          </Form>
          <div className="sbti-note sbti-note--warn">
            <b>Convención declarada:</b> los montos exactos cambian con cada versión del tarifario. Este módulo no
            fija montos: se registran acá al cotizar con SBTi Services.
          </div>

          <h3 style={{ marginTop: 16 }}>Documentación para presentar</h3>
          {DOCS.map(([id, label]) => (
            <Checkbox
              key={id}
              className="sbti-paso6__doc"
              label={label}
              checked={!!(v.documents || {})[id]}
              onChange={(_, d) => setDoc(id, d.checked)}
            />
          ))}
        </div>
      </div>

      <div className="sbti-card">
        <div className="sbti-src">
          <b>Fuentes:</b> SBTi Services — proceso oficial de 6 etapas y Validation Portal; «A beginner's guide to
          target validation»; Criteria Assessment Indicators v1.7; FAQs PYME v6.2 (sin compromiso para PYME);
          tarifario v6.
        </div>
      </div>
    </div>
  );
}
